/**
 * Sequential execution of a prepared sales import.
 *
 * Orders are written one at a time on purpose: every sale merges increments into the same
 * `dailyStats` document for its business day, and concurrent batches on one document
 * contend and fail. Sequential is also what makes the progress counter meaningful and
 * keeps a failure localized to a single order.
 *
 * The write itself is injected (`saveSale`), so this orchestration is unit-testable
 * without Firestore or the network.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- sale payloads and client docs are untyped across read modes */

import { buildSaleInfo, type BuildSaleInfoContext } from './buildSaleInfo'
import { type OrderDraft } from './types'
import { importFailureMessage } from '../../global/Firebase/writeOutcome'

export interface ImportOrderFailure {
  rowNumbers: number[]
  message: string
}

export interface ImportRunResult {
  createdOrders: number
  createdRows: number
  failures: ImportOrderFailure[]
}

export interface RunSalesImportOptions {
  orders: OrderDraft[]
  /** Everything constant for the run: store, org, cashier, batch id, price types. */
  context: Omit<BuildSaleInfoContext, 'client' | 'paymentMethod'>
  /** Client document for an order's phone key, or null to write the sale without a client. */
  resolveClient: (clientKey: string | null) => any | null
  /** Payment method for an order, or null for a postPaid (debt) sale. */
  resolvePaymentMethod: (paymentMethodKey: string | null) => any | null
  /** The actual write — `(info) => saveNewSale(db, info)` in the screen. */
  saveSale: (cartInfo: Record<string, any>) => Promise<unknown>
  onProgress?: (completed: number, total: number) => void
}

const failureMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message: unknown }).message)
    // saveNewSale throws this for a composite product whose ingredient is missing.
    if (message === 'INGREDIENT_NOT_FOUND') {
      const details = error as { ingredientName?: string; productName?: string }
      return `INGREDIENT_NOT_FOUND: ${details.productName ?? ''} / ${details.ingredientName ?? ''}`
    }
    return message
  }
  return String(error)
}

export const runSalesImport = async ({
  orders,
  context,
  resolveClient,
  resolvePaymentMethod,
  saveSale,
  onProgress,
}: RunSalesImportOptions): Promise<ImportRunResult> => {
  const result: ImportRunResult = { createdOrders: 0, createdRows: 0, failures: [] }

  for (let index = 0; index < orders.length; index++) {
    const order = orders[index]
    try {
      const { cartInfo } = buildSaleInfo(order, {
        ...context,
        client: resolveClient(order.clientKey),
        paymentMethod: resolvePaymentMethod(order.paymentMethodKey),
      })
      await saveSale(cartInfo)
      result.createdOrders += 1
      result.createdRows += order.rowNumbers.length
    } catch (error) {
      // One order failing must not abandon the rest: the remaining orders are
      // independent sales, and the report tells the user exactly which rows to retry.
      result.failures.push({ rowNumbers: order.rowNumbers, message: importFailureMessage(error, failureMessage(error)) })
    }
    onProgress?.(index + 1, orders.length)
  }

  return result
}
